import { z } from 'zod';
import {
  RATE_MODE_OPTIONS,
  rateModeDefaults,
  rateModeFields,
  refineRateMode,
} from '../../../playground/endpoints/_shared';
import {
  CONTRIBUTION_PERIODICITY_OPTIONS,
  GENDER_OPTIONS,
  PAYMENT_PERIODICITY_OPTIONS,
  contributionSchema,
} from '../_shared';

export {
  CONTRIBUTION_PERIODICITY_OPTIONS,
  GENDER_OPTIONS,
  PAYMENT_PERIODICITY_OPTIONS,
  RATE_MODE_OPTIONS,
};
export type { ContributionFormValue } from '../_shared';

// `src/v2/common/enums.py::Regime` -- A earns the Bonificación Fiscal, B the
// annual income-tax deduction. APV Select's own, so it stays here rather than
// in the shared option lists.
export const REGIME_OPTIONS = [
  { value: 'A', label: 'Régimen A' },
  { value: 'B', label: 'Régimen B' },
] as const;

// Business-rule constants transcribed from
// `src/v2/products/insurance/apv_select/constants.py` (cloud-functions repo)
// so the form can pre-validate against the same floors/ceilings the backend
// enforces, instead of only discovering them via a 400.
export const DEATH_CAPITAL_MIN_UF = 300;
export const DEATH_CAPITAL_MIN_PCT_OF_INITIAL = 0.05;
export const DEATH_CAPITAL_MAX_TOTAL_UF = 3000;
export const ACCIDENTAL_DEATH_CAPITAL_MIN_UF = 300;
export const INITIAL_LUMP_SUM_MIN_UF = 600;
export const MAX_PROJECTION_RATE = 0.05;
// `REGIME_B_ANNUAL_DEDUCTION_CAP_UF` -- not a validation: the backend caps
// the deductible base at this and reports the rest as `excess_amount`, it
// never rejects the input. Surfaced as a hint only.
export const REGIME_B_ANNUAL_DEDUCTION_CAP_UF = 600;
// `fiscal/fiscal_bonus.py` -- Régimen A's credit and its own annual ceiling.
export const FISCAL_BONUS_RATE = 0.15;
export const FISCAL_BONUS_CAP_UTM = 6;

export const apvSelectSchema = z.object({
  date_of_birth: z
    .string()
    .min(1, 'Requerido')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato YYYY-MM-DD'),
  gender: z.enum(['MALE', 'FEMALE', 'OTHER']),
  net_salary: z.coerce.number().nonnegative('Debe ser ≥ 0'),
  regime: z.enum(['A', 'B']),
  ...rateModeFields,
  death_insured_capital: z.coerce.number().nonnegative(),
  accidental_death_insured_capital: z.coerce.number().nonnegative(),
  itp_insured_capital: z.coerce.number().nonnegative(),
  contributions: z.array(contributionSchema),
  incoming_apv_transfer: z.coerce.number().nonnegative(),
  agreed_deposits: z.array(contributionSchema),
  agreed_premium: z.coerce.number().nonnegative(),
  payment_periodicity: z.enum(['monthly', 'quarterly', 'semiannual', 'annual']),
})
  .superRefine(refineRateMode)
  .superRefine((values, ctx) => {
    // Mirrors `initial_lump_sum.assert_initial_lump_sum_floor`: only money
    // that lands at inception counts, deferred contributions do not.
    const inceptionContributions = values.contributions
      .filter((c) => c.start_month === 1)
      .reduce((sum, c) => sum + c.amount, 0);
    const inceptionAgreedDeposits = values.agreed_deposits
      .filter((c) => c.start_month === 1)
      .reduce((sum, c) => sum + c.amount, 0);
    const initialLumpSum = values.agreed_premium + inceptionContributions
      + values.incoming_apv_transfer + inceptionAgreedDeposits;

    if (initialLumpSum < INITIAL_LUMP_SUM_MIN_UF) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Aporte Único Inicial (prima pactada + aportes/depósitos de inicio + traspaso) debe ser ≥ ${INITIAL_LUMP_SUM_MIN_UF} UF, hay ${initialLumpSum}`,
        path: ['incoming_apv_transfer'],
      });
    }

    // `coverage_capital.assert_death_capital_floor`: the greater of the flat
    // floor and a percentage of the Aporte Único Inicial.
    const deathFloor = Math.max(
      DEATH_CAPITAL_MIN_UF,
      DEATH_CAPITAL_MIN_PCT_OF_INITIAL * initialLumpSum,
    );
    if (values.death_insured_capital < deathFloor) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Mínimo ${deathFloor.toFixed(2)} UF (mayor entre ${DEATH_CAPITAL_MIN_UF} UF y ${DEATH_CAPITAL_MIN_PCT_OF_INITIAL * 100}% del Aporte Único Inicial)`,
        path: ['death_insured_capital'],
      });
    }

    const combinedDeathCapital = values.death_insured_capital
      + values.accidental_death_insured_capital;
    if (combinedDeathCapital > DEATH_CAPITAL_MAX_TOTAL_UF) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Muerte + muerte accidental no debe superar ${DEATH_CAPITAL_MAX_TOTAL_UF} UF`,
        path: ['death_insured_capital'],
      });
    }

    if (
      values.accidental_death_insured_capital > 0
      && values.accidental_death_insured_capital < ACCIDENTAL_DEATH_CAPITAL_MIN_UF
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Mínimo ${ACCIDENTAL_DEATH_CAPITAL_MIN_UF} UF (o 0 para no contratar)`,
        path: ['accidental_death_insured_capital'],
      });
    }

    const itpMismatchesDeathCapital = values.itp_insured_capital > 0
      && values.itp_insured_capital !== values.death_insured_capital;
    if (itpMismatchesDeathCapital) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Debe ser igual al capital de muerte, o 0 para no contratar',
        path: ['itp_insured_capital'],
      });
    }

    // `validators/common.assert_projection_rate_within_maximum` checks every
    // tier of the resolved schedule, not just a flat rate, so a stepped
    // schedule can't smuggle in a rate a fixed one would be refused.
    if (values.rate_mode === 'fixed') {
      if ((values.projection_rate ?? 0) > MAX_PROJECTION_RATE) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Máximo ${MAX_PROJECTION_RATE * 100}%`,
          path: ['projection_rate'],
        });
      }
      return;
    }
    (values.projection_rate_schedule ?? []).forEach((tier, index) => {
      if (tier.annual_rate > MAX_PROJECTION_RATE) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Máximo ${MAX_PROJECTION_RATE * 100}%`,
          path: ['projection_rate_schedule', index, 'annual_rate'],
        });
      }
    });
  });

export type ApvSelectFormValues = z.infer<typeof apvSelectSchema>;

export type ApvSelectRegime = ApvSelectFormValues['regime'];

export const isFixedRate = (v: ApvSelectFormValues) => v.rate_mode === 'fixed';
export const isSteppedRate = (v: ApvSelectFormValues) => v.rate_mode === 'stepped';

export const apvSelectDefaults: ApvSelectFormValues = {
  date_of_birth: '1990-01-01',
  gender: 'MALE',
  net_salary: 1500000,
  regime: 'A',
  ...rateModeDefaults,
  projection_rate: 0.0331,
  death_insured_capital: DEATH_CAPITAL_MIN_UF,
  accidental_death_insured_capital: 0,
  itp_insured_capital: 0,
  contributions: [{ amount: 5, periodicity: 'monthly', start_month: 1 }],
  incoming_apv_transfer: 600,
  agreed_deposits: [],
  agreed_premium: 0,
  payment_periodicity: 'monthly',
};
